import * as React from "react";
import { Dispatch, AnyAction } from "redux";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import _ from "lodash";
import { CountriesStreamsProps, CountriesStreams } from "../../components";
import {
    RequiredReportingProps,
    RequiredReportingState,
    RequiredReportingDispatchProps,
    queryRequest,
    TimeRange,
    countryInfo,
    reportingOverviewRequestPayload,
    overviewRequestPayload,
    timeRangePayload,
    EntityType,
    playlist,
    BundleMap,
} from "../../../models";
import {
    RootState,
    reportingActions,
    userActions,
    playlistCountriesScreenSelector,
} from "../../../store";
import {
    getLocalizedString,
    paths,
    guessTimeZone,
    generateQueryWithoutGranularity,
    getSeeAllCountriesRequestId,
} from "../../../utils";
import { stringIds, bundleIds } from "../../../assets";

const testIDPrefix = "PlaylistCountriesScreen";
const metricPrefix = "playlistCountriesPage";

type ViewProps = {
    asin: string;
};

type StateProps = RequiredReportingProps & {
    topCountries: countryInfo[];
    bundleMap: BundleMap;
};

type DispatchProps = RequiredReportingDispatchProps & {
    getReports: (payload: reportingOverviewRequestPayload) => void;
    updateCurrentPath: (payload: string) => void;
    selectAsin: (asin: string) => void;
};

type Props = DispatchProps & StateProps & RouteComponentProps<ViewProps>;

type State = RequiredReportingState & {
    playlistCatalogItem?: playlist;
};

class PlaylistCountriesScreen extends React.Component<Props, State> {
    constructor(props: any) {
        super(props);
        this.state = {
            refreshing: false,
            showError: false,
        };
        this.props.updateCurrentPath(window.location.pathname);
    }

    componentDidMount() {
        // If artist isnt selected, redirect to select artist page
        if (!this.props.selectedArtist) {
            this.props.history?.replace(paths.artistSelect);
            return;
        }

        const asin = this.props.match.params.asin;
        if (!asin) {
            return;
        }
        window.scrollTo(0, 0);

        this.props.selectAsin(asin);

        this.setState({ finishedInitialLoad: false });

        const playlistCatalogItem = this.props.catalog.get(asin) as playlist;
        if (playlistCatalogItem) {
            this.setState({ playlistCatalogItem: playlistCatalogItem });
        }

        this.getReports();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.selectedRange !== prevProps.selectedRange) {
            this.getReports();
        } else if (
            (this.props.selectedRange === TimeRange.Custom &&
                this.props.startDate !== prevProps.startDate) ||
            this.props.endDate !== prevProps.endDate
        ) {
            this.getReports(true);
        } else if (this.props.selectedArtist !== prevProps.selectedArtist) {
            this.getReports();
        }

        if (this.props.error !== prevProps.error) {
            this.setState({ showError: this.props.error !== undefined });
        }

        if (prevProps.inProgress && !this.props.inProgress) {
            this.setState({ refreshing: false, loadedReports: true });
        }

        // Loaded everything for the first time
        if (
            this.state.finishedInitialLoad === false &&
            this.state.loadedReports &&
            this.props.catalogBuildCompleted &&
            this.props.hydrationInProgress &&
            !prevProps.hydrationInProgress
        ) {
            this.setState({ finishedInitialLoad: true });
        }
    }

    render() {
        const playlist = this.state.playlistCatalogItem;
        const countriesStreamsProps: CountriesStreamsProps = {
            topCountries: this.props.topCountries,
            testIDPrefix: testIDPrefix,
            inProgress: this.props.inProgress,
            refreshing: this.state.refreshing,
            showError: this.state.showError,
            catalogItem: playlist,
            entityLabel: getLocalizedString(this.props.bundleMap, {
                bundleId: bundleIds.REPORTS_STRINGS,
                stringId: stringIds.Reports.playlistLabel,
            }),
            getReports: this.getReports,
        };

        return <CountriesStreams {...countriesStreamsProps} />;
    }

    private getReports = (isRefresh?: boolean) => {
        const asin = this.props.match.params.asin;
        if (
            !this.props.signedIn ||
            !this.props.selectedArtist ||
            !this.props.teamId ||
            !asin
        ) {
            return;
        }

        const query: queryRequest = {
            artistAsin: this.props.selectedArtist,
        };

        const timeRangePayload: timeRangePayload = {
            timeRange: this.props.selectedRange,
            startDate: this.props.startDate,
            endDate: this.props.endDate,
        };

        const requestId = getSeeAllCountriesRequestId(
            "playlist",
            asin,
            timeRangePayload
        );
        query.topCountriesForEntityStreamsQuery = {
            ...generateQueryWithoutGranularity(
                timeRangePayload,
                guessTimeZone()
            ),
            entityType: EntityType.Playlist,
            entityId: asin,
        };

        // Dispatch request
        const payload: overviewRequestPayload = {
            query: query,
            requestPath: paths.songCountries,
            requestId: requestId,
            isRefresh: isRefresh,
            teamId: this.props.teamId,
        };

        this.props.getReport(payload);
    };
}

function mapStateToProps(state: RootState) {
    return playlistCountriesScreenSelector(state, paths.songCountries);
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return {
        getReport: (payload: overviewRequestPayload) =>
            dispatch(reportingActions.getReports(payload)),
        selectAsin: (asin: string) =>
            dispatch(reportingActions.setSelectedPlaylistAsin(asin)),
        updateCurrentPath: (payload: string) =>
            dispatch(userActions.updateCurrentPath(payload)),
    };
}

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(PlaylistCountriesScreen)
);
